/**
 * ZAP-2573: strips internal database-id leakage from AI Analyst prose. The
 * backend prompt asks the model not to surface internal ids in its answers,
 * but streamed narration still sometimes slips them in as parentheticals or
 * bare id phrases — e.g. "a Flight Risk attribute (attribute id 27)", "the
 * Department dimension (id: 3)", "dataframe 509", "periodId 3".
 *
 * Pure and intended for render time only. Callers must NOT persist the
 * stripped text back into anything that gets replayed to the backend as
 * conversation history (ChatInterface's sendMessage builds `apiMessages`
 * from live message state), or the model's own prior turn comes back to it
 * altered on the next request.
 */

// "(attribute id 27)", "(dimension id: 3)", "(id: 16)", "(fact ids 4)" — the
// leading \s* also swallows the space before "(" so the removal reads clean.
// Case-insensitive.
const PARENTHETICAL_ID_RE =
	/\s*\((?:attribute |dimension |dataframe |fact |measure |period )?ids?\s*[:=]?\s*\d+\)/gi;

// "periodId 3" / "period_id=3" / "period_id 3" — case-sensitive so a table
// header or label like "Period ID" (different case/spacing) is left alone.
const BARE_PERIOD_ID_RE = /\bperiodId\s*\d+\b|\bperiod_id\s*=?\s*\d+\b/g;

// "dataframe 509" / "dataframe id 27" — only when NOT immediately followed
// by another digit, so a larger, unrelated number is never eaten into.
const DATAFRAME_ID_RE = /\bdataframe(?: id)? \d+(?!\d)/g;

export const stripInternalIds = (text: string): string => {
	if (!text) return text;

	return text
		.replace(PARENTHETICAL_ID_RE, '')
		.replace(BARE_PERIOD_ID_RE, '')
		.replace(DATAFRAME_ID_RE, '')
		.replace(/ {2,}/g, ' '); // collapse the double space a mid-sentence strip can leave; newlines (markdown tables) are untouched
};
